import React, { useState, useEffect } from 'react';
import './../App.css';
import LiveStreamBanner from '../components/LiveStreamBanner';
import useTwitchStatus from '../hooks/useTwitchStatus';
import {
  DAY_KEYS,
  DAY_LABELS,
  streamInstancesForDate,
  findNextStream,
  weekOffsetFrom,
} from '../utils/weeklySchedule';

const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const getLocalTimeParts = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  return {
    hour: parts.find((p) => p.type === 'hour').value,
    minute: parts.find((p) => p.type === 'minute')?.value ?? '00',
    meridiem: (parts.find((p) => p.type === 'dayPeriod')?.value || '').toLowerCase(),
  };
};

// Always shows am/pm on both ends (e.g. "7am–9am", "11am–4pm") so every
// range reads the same way regardless of whether it happens to cross noon.
// Drops the ":00" only when both ends land on the hour, e.g. "7am–9am"
// instead of "7:00am–9:00am".
const formatLocalRange = (startTime, endTime, includeAbbr = false) => {
  const start = getLocalTimeParts(startTime);
  const end = getLocalTimeParts(endTime);
  const bothOnTheHour = start.minute === '00' && end.minute === '00';

  const startLabel = bothOnTheHour ? start.hour : `${start.hour}:${start.minute}`;
  const endLabel = bothOnTheHour ? end.hour : `${end.hour}:${end.minute}`;
  const range = `${startLabel}${start.meridiem}–${endLabel}${end.meridiem}`;

  if (!includeAbbr) return range;
  const abbr =
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(startTime)
      .find((part) => part.type === 'timeZoneName')?.value || '';
  return `${range} ${abbr}`.trim();
};

const isSameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function WeeklySchedule() {
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const twitchStatus = useTwitchStatus();

  useEffect(() => {
    fetch('/weeklySchedule.json')
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load schedule');
        return response.json();
      })
      .then((data) => {
        setScheduleData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Local one-second tick drives the countdown and past/upcoming states.
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-retro-bg retro-grid flex items-center justify-center">
        <div className="retro-text text-2xl">Loading schedule...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-retro-bg retro-grid flex items-center justify-center">
        <div className="retro-text text-2xl">Error: {error}</div>
      </div>
    );
  }

  const categoryColors = scheduleData.categories || {};
  const nextStream = findNextStream(scheduleData, now);

  // Rolling window: a couple of days back for reference, today, and the
  // rest of the upcoming week — 8 cards total, balanced around today
  // rather than crowded toward the future.
  const PAST_DAYS = 2;
  const FUTURE_DAYS = 5;
  const windowStart = new Date(now);
  windowStart.setHours(0, 0, 0, 0);
  windowStart.setDate(windowStart.getDate() - PAST_DAYS);
  const weekDays = Array.from({ length: PAST_DAYS + 1 + FUTURE_DAYS }, (_, index) => {
    const date = new Date(windowStart);
    date.setDate(windowStart.getDate() + index);
    const weekOffset = weekOffsetFrom(now, date);
    const isToday = index === PAST_DAYS;
    const streams = streamInstancesForDate(scheduleData, date);
    // Once today's last stream ends, mute the column like other past days
    // so a finished Friday morning doesn't stay highlighted all evening.
    const isPastDay =
      index < PAST_DAYS ||
      (isToday && streams.length > 0 && streams.every((stream) => stream.endTime <= now));
    return {
      dayKey: DAY_KEYS[(date.getDay() + 6) % 7],
      date,
      isToday,
      isPastDay,
      weekOffset,
      // Soft labels when the rolling window crosses a week boundary —
      // useful on Friday when next week's slots are already visible.
      weekLabel:
        weekOffset > 0 ? 'Next week' : weekOffset < 0 ? 'Last week' : null,
      streams,
    };
  });

  const countdown = (() => {
    if (!nextStream) return null;
    const diff = nextStream.startTime.getTime() - now.getTime();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  })();

  const isNextStreamInstance = (instance) =>
    nextStream &&
    instance.dayKey === nextStream.dayKey &&
    instance.startTime.getTime() === nextStream.startTime.getTime() &&
    isSameLocalDay(instance.date, nextStream.date);

  // Categories are shown as colored dots on each card instead of repeating
  // the full name every time; this legend is the one place that name still
  // appears. Rendered inside both the mobile and desktop panels below
  // (never both at once, so no duplicate IDs on screen).
  const visibleCategories = Array.from(
    new Set(weekDays.flatMap((day) => day.streams.map((stream) => stream.category)))
  );
  const nextStreamVisibleInWindow = weekDays.some((day) =>
    day.streams.some(isNextStreamInstance)
  );
  const categoryLegend = (visibleCategories.length > 0 || nextStreamVisibleInWindow) && (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mb-4">
      {visibleCategories.map((category) => {
        const color = categoryColors[category];
        return (
          <div key={category} className="flex items-center gap-1.5">
            {color && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`} />}
            <span className="retro-text text-retro-muted text-xs">{category}</span>
          </div>
        );
      })}
      {nextStreamVisibleInWindow && (
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border-2 border-retro-purple flex-shrink-0" />
          <span className="retro-text text-retro-muted text-xs">Next stream</span>
        </div>
      )}
    </div>
  );

  // Mobile list chip. The desktop grid renders its own card markup below —
  // it needs a single card per day (not one per stream) so every card in a
  // row can share the same height, which this per-stream chip can't do.
  const renderMobileStreamChip = (instance) => {
    const categoryColor = categoryColors[instance.category];
    const isPast = instance.endTime <= now;
    const isNext = isNextStreamInstance(instance);

    return (
      <div
        key={`${instance.dayKey}-${instance.time}-${instance.subject}`}
        className={`rounded-lg border-2 flex flex-col items-center p-3 min-w-0 transition-all duration-200 ${
          isNext
            ? 'bg-retro-purple/15 border-retro-purple'
            : categoryColor
              ? `${categoryColor.bg} ${categoryColor.border}`
              : 'bg-gray-50 border-gray-300'
        } ${isPast ? 'opacity-60' : ''}`}
      >
        {/* Category is shown as a colored dot (see legend) rather than
            repeating its full name on every card — the name is identical
            across most streams and was the biggest source of clutter.
            Dot-then-title (not side-by-side) matches the desktop cards. */}
        {categoryColor && (
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${categoryColor.dot}`}
            title={instance.category}
          />
        )}
        <div
          className={`text-sm font-bold leading-snug text-center break-words mt-2 min-w-0 ${
            isNext ? 'text-retro-text' : 'text-gray-800'
          }`}
        >
          {instance.subject}
        </div>
        <div
          className={`text-xs leading-tight whitespace-nowrap mt-1 ${
            isNext ? 'text-retro-purple' : 'text-gray-600'
          }`}
        >
          {formatLocalRange(instance.startTime, instance.endTime)}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-retro-bg retro-grid scanline p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <div className="retro-container p-6 retro-glow mb-4">
            <h1 className="retro-title text-lg sm:text-xl md:text-2xl lg:text-3xl mb-2 leading-tight">
              ITSFLANNELBEARD'S<br />
              WEEKLY STREAM SCHEDULE
            </h1>
            <p className="retro-text text-retro-muted text-xs sm:text-sm font-mono mt-2">
              All times shown in your timezone: {getUserTimezone()}
            </p>
          </div>
        </div>

        {/* Live Stream Banner */}
        <LiveStreamBanner twitchStatus={twitchStatus} />

        {/* Next Stream Card - Show when not live (or while loading Twitch status) */}
        {nextStream && (!twitchStatus.isLive || twitchStatus.loading) && (
          <div className="mb-6 retro-container p-6 retro-glow border-retro-purple">
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-bold text-retro-text mb-2">
                Next Stream
              </h2>
              <p className="text-lg md:text-xl text-retro-text mb-2">
                <span className="font-semibold">{nextStream.subject}</span>
              </p>
              <p className="text-md md:text-lg text-retro-muted mb-3">
                {nextStream.category} •{' '}
                {formatLocalRange(nextStream.startTime, nextStream.endTime, true)}
              </p>
              <p className="text-sm text-retro-muted mb-3">
                {new Intl.DateTimeFormat('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                }).format(nextStream.startTime)}
              </p>

              {countdown && (
                <div className="mb-4 p-4 countdown-timer">
                  <div className="text-center relative z-10">
                    <div className="text-sm font-semibold countdown-label mb-3">
                      Stream starts in:
                    </div>
                    <div className="flex justify-center space-x-3">
                      {countdown.days > 0 && (
                        <div className="flex flex-col items-center">
                          <div className="countdown-time-box px-3 py-2 text-lg">
                            {countdown.days}
                          </div>
                          <div className="countdown-time-label mt-1">days</div>
                        </div>
                      )}
                      <div className="flex flex-col items-center">
                        <div className="countdown-time-box px-3 py-2 text-lg">
                          {countdown.hours.toString().padStart(2, '0')}
                        </div>
                        <div className="countdown-time-label mt-1">hours</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="countdown-time-box px-3 py-2 text-lg">
                          {countdown.minutes.toString().padStart(2, '0')}
                        </div>
                        <div className="countdown-time-label mt-1">min</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="countdown-time-box px-3 py-2 text-lg">
                          {countdown.seconds.toString().padStart(2, '0')}
                        </div>
                        <div className="countdown-time-label mt-1">sec</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <a
                href={`https://www.twitch.tv/${twitchStatus.channelName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-[#9146FF] hover:bg-[#7c3ae0] text-white font-bold rounded-lg shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                ❤️ Follow on Twitch
              </a>
            </div>
          </div>
        )}

        {/* Stacked list — below md, where fixed-width cards would drop below
            a ~4-column wrap into something too cramped to be useful. */}
        <div className="block md:hidden">
          <div className="retro-container p-4 retro-glow">
            <h2 className="text-lg font-bold text-retro-text mb-4 text-center">
              Recent & Upcoming
            </h2>
            {categoryLegend}
            <div className="space-y-4">
              {weekDays.map(({ dayKey, date, isToday, isPastDay, weekLabel, streams }) => (
                <div key={date.toDateString()} className={isPastDay ? 'opacity-60' : ''}>
                  <div
                    className={`text-sm font-bold mb-2 ${
                      isToday && !isPastDay ? 'text-retro-cyan' : 'text-retro-muted'
                    }`}
                  >
                    {DAY_LABELS[dayKey].toUpperCase()}{' '}
                    <span className="font-normal">
                      {new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                      }).format(date)}
                    </span>
                    {isToday && ' · TODAY'}
                    {weekLabel && !isToday && (
                      <span className="font-normal text-retro-muted"> · {weekLabel}</span>
                    )}
                  </div>
                  {streams.length > 0 ? (
                    <div className="space-y-2">{streams.map(renderMobileStreamChip)}</div>
                  ) : (
                    <div
                      className={`p-3 rounded-lg border-2 text-xs text-center uppercase tracking-wide text-retro-muted ${
                        isToday && !isPastDay ? 'border-retro-cyan' : 'border-dashed border-retro-border'
                      }`}
                    >
                      Off air
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop week row — md+ allows a ~4-column wrap; below that we
            switch to the stacked list instead of a 3-wide (or narrower) grid. */}
        <div className="hidden md:block">
          <div className="retro-container p-4 md:p-6 retro-glow">
            {categoryLegend}
            {/* Fixed-width cards (w-32 / 8rem) so size and text stay put as the
                viewport shrinks; flex-wrap moves them to a new row when another
                card no longer fits, instead of resizing or horizontal scroll.
                8rem fits a 12-letter word like "announcement" at 13px. */}
            <div className="flex flex-wrap gap-2 justify-center">
              {weekDays.map(({ dayKey, date, isToday, isPastDay, weekLabel, streams }) => {
                const isOffAir = streams.length === 0;
                const dayHasNext = streams.some(isNextStreamInstance);
                // Same compact format as the header/next-stream card; joined
                // with " / " on the rare day with more than one stream.
                const headerTime = streams
                  .map((s) => formatLocalRange(s.startTime, s.endTime))
                  .join(' / ');

                // Cards share one dark background regardless of category —
                // the category dot is the only per-category indicator — so
                // only the next-stream and off-air states change the card.
                let cardBgClass;
                let cardBorderClass;
                if (dayHasNext) {
                  cardBgClass = 'bg-retro-purple/15';
                  cardBorderClass = 'border-retro-purple';
                } else if (isOffAir) {
                  cardBgClass = 'bg-retro-bg/30';
                  cardBorderClass =
                    isToday && !isPastDay
                      ? 'border-retro-cyan'
                      : 'border-dashed border-retro-border';
                } else {
                  cardBgClass = 'bg-retro-surface';
                  cardBorderClass = 'border-retro-border';
                }

                return (
                  <div
                    key={date.toDateString()}
                    className={`w-32 shrink-0 flex flex-col ${isPastDay ? 'opacity-60' : ''}`}
                  >
                    {/* Header uses fixed-height slots for the TODAY label and
                        time (always rendered, just empty when not applicable)
                        so every column's header is exactly the same height —
                        otherwise the card below gets whatever height is left
                        over and cards in the same row end up different sizes. */}
                    <div className="text-center py-2">
                      <div
                        className={`text-sm font-bold ${
                          isToday && !isPastDay ? 'text-retro-cyan' : 'text-retro-muted'
                        }`}
                      >
                        {DAY_LABELS[dayKey].slice(0, 3)}
                      </div>
                      <div className="text-xs text-retro-muted">
                        {new Intl.DateTimeFormat('en-US', {
                          month: 'short',
                          day: 'numeric',
                        }).format(date)}
                      </div>
                      <div
                        className={`h-3.5 text-[10px] font-bold tracking-wide ${
                          isToday && !isPastDay ? 'text-retro-cyan' : 'text-retro-muted'
                        }`}
                      >
                        {isToday ? 'TODAY' : weekLabel ? weekLabel.toUpperCase() : ''}
                      </div>
                      <div
                        className={`h-[18px] text-sm font-bold whitespace-nowrap ${
                          dayHasNext ? 'text-retro-purple' : 'text-retro-muted'
                        }`}
                      >
                        {headerTime}
                      </div>
                    </div>
                    <div
                      className={`flex-grow flex flex-col items-center min-h-[150px] py-3.5 px-3 rounded-lg border-2 transition-all duration-200 ${cardBgClass} ${cardBorderClass}`}
                    >
                      {isOffAir ? (
                        <div className="flex-1 flex items-center justify-center text-[11px] font-semibold uppercase tracking-wide text-retro-muted">
                          Off air
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 w-full min-w-0">
                          {streams.map((instance) => {
                            const dotColor = categoryColors[instance.category];
                            return (
                              <div
                                key={`${instance.dayKey}-${instance.time}-${instance.subject}`}
                                className="flex flex-col items-center w-full min-w-0"
                              >
                                {dotColor && (
                                  <span
                                    className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${dotColor.dot}`}
                                    title={instance.category}
                                  />
                                )}
                                {/* Fixed margin-top (not vertically centered) keeps
                                    every card's title starting on the same line,
                                    whether it wraps to one line or three. */}
                                <span className="text-[13px] font-semibold leading-snug text-center break-words mt-[18px] min-w-0 text-retro-text">
                                  {instance.subject}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
